import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import {
    Box,
    Card,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    CircularProgress,
    Alert,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import StorageIcon from '@mui/icons-material/Storage'
import SaveIcon from '@mui/icons-material/Save'
import { productService, inventoryService } from '../services'
import CachedImage from '../components/CachedImage'
import {
  validatePositiveSellingPriceAndMrp,
} from '../utils/sellingPriceMrp'
import {
    getAvailableQuantity,
    resolveInventoryDetailReturnPath,
    validateInventoryQuantities,
} from '../utils/inventoryManagementUtils'

const InventoryDetailPage = () => {
    const { id } = useParams() // lot UUID
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()

    // State passed from the inventory list row
    const stateData = location.state || {}

    const handleBackToInventory = useCallback(() => {
        const returnPath = resolveInventoryDetailReturnPath(searchParams, stateData)
        if (returnPath) {
            navigate(returnPath)
            return
        }
        navigate(-1)
    }, [navigate, searchParams, stateData])

    const [product, setProduct] = useState(null)
    const [inventory, setInventory] = useState(null)
    const [movements, setMovements] = useState([])
    const [movementsTotal, setMovementsTotal] = useState(0)
    const [movementsPage, setMovementsPage] = useState(0)
    const [movementsRowsPerPage, setMovementsRowsPerPage] = useState(10)

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    // Edit dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [editFormData, setEditFormData] = useState({})
    const [actionLoading, setActionLoading] = useState(false)
    const [actionError, setActionError] = useState(null)
    const [barcodes, setBarcodes] = useState([])

    const editQuantityError = useMemo(
        () => validateInventoryQuantities(editFormData.quantity_on_hand, editFormData.reserved_quantity),
        [editFormData.quantity_on_hand, editFormData.reserved_quantity],
    )

    const editAvailableQuantity = useMemo(
        () => getAvailableQuantity(editFormData.quantity_on_hand, editFormData.reserved_quantity),
        [editFormData.quantity_on_hand, editFormData.reserved_quantity],
    )

    // ── Fetch all 3 APIs ─────────────────────────────────────────────────────
    const fetchAll = useCallback(async (lotIdParam, productIdParam) => {
        try {
            setLoading(true)
            setError(null)
            setBarcodes([])

            const [productRes, inventoryRes] = await Promise.all([
                productIdParam ? productService.getProductById(productIdParam).catch(() => null) : Promise.resolve(null),
                lotIdParam ? inventoryService.getInventoryLotById(lotIdParam).catch(() => null) : Promise.resolve(null),
            ])

            setProduct(productRes || null)
            setInventory(inventoryRes || null)

            const resolvedProductId =
                productRes?.id ||
                inventoryRes?.adminProduct?.id ||
                productIdParam ||
                null

            if (resolvedProductId) {
                try {
                    const barcodeResponse = await productService.getProductBarcodes(resolvedProductId)
                    const barcodeValues = (barcodeResponse?.items || [])
                        .map((item) => item?.barcode)
                        .filter((v) => typeof v === 'string' && v.trim().length > 0)
                    setBarcodes(barcodeValues)
                } catch {
                    const fromLot = inventoryRes?.adminProduct?.barcodes
                    if (Array.isArray(fromLot) && fromLot.length > 0) {
                        setBarcodes(fromLot.filter((v) => typeof v === 'string' && v.trim().length > 0))
                    } else if (inventoryRes?.adminProduct?.barcode) {
                        setBarcodes([String(inventoryRes.adminProduct.barcode).trim()])
                    } else {
                        setBarcodes([])
                    }
                }
            } else {
                setBarcodes([])
            }
        } catch (err) {
            console.error('Error fetching inventory detail:', err)
            setError(err.response?.data?.message || 'Failed to fetch inventory details')
        } finally {
            setLoading(false)
        }
    }, [])

    const fetchMovements = useCallback(async (lotIdParam) => {
        if (!lotIdParam) return
        try {
            const response = await inventoryService.getInventoryLotMovements(lotIdParam, {
                page: movementsPage + 1,
                limit: movementsRowsPerPage,
            })
            const data = Array.isArray(response) ? response : (response.items || response.data || [])
            const total = response.pagination?.total || data.length
            setMovements(data)
            setMovementsTotal(total)
        } catch {
            setMovements([])
            setMovementsTotal(0)
        }
    }, [movementsPage, movementsRowsPerPage])

    // On mount — use state data to resolve productId immediately if available
    useEffect(() => {
        const resolvedProductId = stateData.productId || stateData.adminProductId
        if (id) {
            fetchAll(id, resolvedProductId)
        } else {
            setError('Could not determine which inventory lot to show.')
            setLoading(false)
        }
    }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (id) fetchMovements(id)
    }, [id, movementsPage, movementsRowsPerPage, fetchMovements]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Edit handlers ────────────────────────────────────────────────────────
    const handleEditClick = () => {
        setEditFormData({
            quantity_on_hand: parseInt(inventory?.quantity_on_hand, 10) || 0,
            minimum_stock_level: parseInt(inventory?.minimum_stock_level, 10) || 0,
            maximum_stock_level: parseInt(inventory?.maximum_stock_level, 10) || 0,
            reorder_point: parseInt(inventory?.reorder_point, 10) || 0,
            reserved_quantity: parseInt(inventory?.reserved_quantity, 10) || 0,
            unit_cost: parseFloat(inventory?.unit_cost) || 0,
            unit_price: parseFloat(inventory?.unit_price) || 0,
            location: inventory?.location || '',
            notes: inventory?.notes || '',
        })
        setActionError(null)
        setEditDialogOpen(true)
    }

    const handleEditClose = () => {
        setEditDialogOpen(false)
        setEditFormData({})
        setActionError(null)
    }

    const handleFieldChange = (field, value) => {
        let parsed = value
        if (['quantity_on_hand', 'minimum_stock_level', 'maximum_stock_level', 'reorder_point', 'reserved_quantity'].includes(field)) {
            parsed = parseInt(value, 10) || 0
        } else if (['unit_cost', 'unit_price'].includes(field)) {
            parsed = parseFloat(value) || 0
        }
        setEditFormData(prev => ({ ...prev, [field]: parsed }))
    }

    const handleSaveEdit = async () => {
        try {
            setActionLoading(true)
            setActionError(null)

            const quantityError = validateInventoryQuantities(
                editFormData.quantity_on_hand,
                editFormData.reserved_quantity,
            )
            if (quantityError) {
                setActionError(quantityError)
                return
            }
            const priceError = validatePositiveSellingPriceAndMrp(
                editFormData.unit_cost,
                editFormData.unit_price,
                { requireBoth: true },
            )
            if (priceError) {
                setActionError(priceError)
                return
            }

            // Use the lot UUID (id from URL) to target the exact lot
            await inventoryService.updateAdminInventoryByLotId(id, {
                ...editFormData,
                reason: 'Manual update from inventory detail page',
                metadata: {
                    source: 'inventory_detail_page',
                    inventory_id: id,
                    sku: stateData.sku || inventory?.sku,
                    updated_at: new Date().toISOString(),
                },
            })

            setSuccessMessage('Inventory updated successfully')
            setTimeout(() => setSuccessMessage(null), 3000)
            handleEditClose()

            // Refresh inventory data
            const resolvedProductId = stateData.productId || stateData.adminProductId
            await fetchAll(id, resolvedProductId)
            await fetchMovements(id)
        } catch (err) {
            console.error('Error updating inventory:', err)
            setActionError(err.response?.data?.message || 'Failed to update inventory')
        } finally {
            setActionLoading(false)
        }
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    const formatDate = (d) => {
        if (!d) return 'N/A'
        return new Date(d).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        })
    }

    const getMovementColor = (type) => {
        const map = {
            create: { bg: '#e8f5e9', color: '#2e7d32' },
            update: { bg: '#e3f2fd', color: '#1565c0' },
            stock_in: { bg: '#e8f5e9', color: '#2e7d32' },
            stock_out: { bg: '#fff3e0', color: '#e65100' },
            adjustment: { bg: '#f3e5f5', color: '#7b1fa2' },
            correction: { bg: '#ffebee', color: '#c62828' },
        }
        return map[type] || { bg: '#f5f5f5', color: '#757575' }
    }

    const getStockStatus = (inv) => {
        if (!inv) return { label: 'Unknown', color: 'default' }
        const qty = parseInt(inv.quantity_on_hand) || 0
        const minLevel = parseInt(inv.minimum_stock_level) || 0
        const reorder = parseInt(inv.reorder_point) || 0
        if (qty === 0) return { label: 'Out of Stock', color: 'error' }
        if (reorder > 0 && qty <= reorder) return { label: 'Reorder', color: 'warning' }
        if (qty < minLevel) return { label: 'Low Stock', color: 'warning' }
        return { label: 'In Stock', color: 'success' }
    }

    // ── Render ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress sx={{ color: '#e90000' }} />
            </Box>
        )
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={handleBackToInventory} sx={{ mt: 2 }}>
                    Back to Inventory
                </Button>
            </Box>
        )
    }

    const stockStatus = getStockStatus(inventory)
    const computedAvailable = inventory
        ? (inventory.computed_available_quantity ?? (inventory.quantity_on_hand - (inventory.reserved_quantity || 0)))
        : 0

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={handleBackToInventory} sx={{ mb: 2 }}>
                    Back to Inventory
                </Button>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                    {product?.product_name || stateData.productName || 'Inventory Detail'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                    Article Code: {inventory?.sku || stateData.sku}
                    {inventory?.location ? ` • ${inventory.location}` : ''}
                </Typography>
            </Box>

            {successMessage && (
                <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
                    {successMessage}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Left column */}
                <Grid item xs={12} md={8}>

                    {/* Product Information */}
                    {product && (
                        <Card sx={{ p: 3, mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Product Information</Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => navigate(`/products/${product.id}`)}
                                    sx={{ color: '#e90000', borderColor: '#e90000' }}
                                >
                                    View Product
                                </Button>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Product Name</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{product.product_name}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Article Code</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>{product.sku}</Typography>
                                </Grid>
                                {product.brand && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Brand</Typography>
                                        <Typography variant="body1">{product.brand}</Typography>
                                    </Grid>
                                )}
                                {product.category && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Category</Typography>
                                        <Typography variant="body1">{product.category}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">MRP</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#e90000' }}>
                                        ৳{parseFloat(inventory?.unit_price || 0).toFixed(2)}
                                    </Typography>
                                </Grid>
                                {inventory?.unit_cost != null && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">Selling Price</Typography>
                                        <Typography variant="body1">৳{parseFloat(inventory.unit_cost).toFixed(2)}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Status</Typography>
                                    <Chip
                                        label={product.status}
                                        size="small"
                                        sx={{
                                            backgroundColor: product.status === 'published' ? '#e8f5e9' : '#fff3e0',
                                            color: product.status === 'published' ? '#2e7d32' : '#e65100',
                                            fontWeight: 500,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    )}

                    {(product || inventory?.adminProduct) && (
                        <Card sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Barcodes
                            </Typography>
                            <Box
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    p: 2,
                                    bgcolor: '#fafafa',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {barcodes.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No barcodes assigned.
                                        </Typography>
                                    ) : (
                                        barcodes.map((b) => (
                                            <Chip
                                                key={b}
                                                label={b}
                                                size="small"
                                                sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                                            />
                                        ))
                                    )}
                                </Box>
                            </Box>
                        </Card>
                    )}

                    {/* Inventory Information */}
                    <Card sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>Inventory Information</Typography>
                            <Button
                                startIcon={<StorageIcon />}
                                size="small"
                                onClick={handleEditClick}
                                disabled={!inventory}
                                sx={{ color: '#e90000', textTransform: 'none', fontWeight: 500 }}
                            >
                                Update Inventory
                            </Button>
                        </Box>

                        {inventory ? (
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Quantity on Hand</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#e90000' }}>
                                        {Math.floor(inventory.quantity_on_hand)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Status</Typography>
                                    <Chip
                                        label={stockStatus.label}
                                        size="small"
                                        sx={{
                                            backgroundColor: stockStatus.label === 'In Stock' ? '#4caf50' : (stockStatus.label === 'Out of Stock' ? '#f44336' : '#ff9800'),
                                            color: '#fff',
                                            fontWeight: 500,
                                            mt: 0.5
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Minimum Stock Level</Typography>
                                    <Typography variant="body1">{inventory.minimum_stock_level ?? '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Maximum Stock Level</Typography>
                                    <Typography variant="body1">{inventory.maximum_stock_level ?? '-'}</Typography>
                                </Grid>

                                {inventory.location && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Location</Typography>
                                        <Typography variant="body1">{inventory.location}</Typography>
                                    </Grid>
                                )}

                                {inventory.notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                                        <Typography variant="body1">{inventory.notes}</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        ) : (
                            <Alert severity="info">No inventory record found for this lot.</Alert>
                        )}
                    </Card>

                    {/* Movement History */}
                    <Card sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                            Inventory Movement History
                        </Typography>

                        {movements.length > 0 ? (
                            <>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Before</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>After</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Change</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Performed By</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {movements.map((m) => {
                                                const change = m.quantity_after - m.quantity_before
                                                const { bg, color } = getMovementColor(m.movement_type)
                                                return (
                                                    <TableRow key={m.id} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                                                        <TableCell>{formatDate(m.created_at)}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={m.movement_type}
                                                                size="small"
                                                                sx={{ backgroundColor: bg, color, fontWeight: 500 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">{m.quantity_before}</TableCell>
                                                        <TableCell align="right">{m.quantity_after}</TableCell>
                                                        <TableCell
                                                            align="right"
                                                            sx={{ color: change >= 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}
                                                        >
                                                            {change >= 0 ? '+' : ''}{change}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#666', maxWidth: 200 }}>{m.reason || '-'}</TableCell>
                                                        <TableCell sx={{ color: '#666', fontSize: '0.8rem' }}>{m.performed_by_email || '-'}</TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={movementsTotal}
                                    rowsPerPage={movementsRowsPerPage}
                                    page={movementsPage}
                                    onPageChange={(_, p) => setMovementsPage(p)}
                                    onRowsPerPageChange={(e) => { setMovementsRowsPerPage(parseInt(e.target.value, 10)); setMovementsPage(0) }}
                                />
                            </>
                        ) : (
                            <Alert severity="info">No movement history found for this article code.</Alert>
                        )}
                    </Card>
                </Grid>

                {/* Right column — product image */}
                <Grid item xs={12} md={4}>
                    {product?.image_url && (
                        <Card sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                Product Images
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Logo Image */}
                                {product.image_url.logo && (
                                    <Paper sx={{ p: 1, border: '1px solid #e0e0e0' }}>
                                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Logo</Typography>
                                        <CachedImage
                                            src={product.image_url.logo}
                                            alt={`${product.product_name} - Logo`}
                                            style={{
                                                width: '100%',
                                                maxWidth: '300px',
                                                height: 'auto',
                                                maxHeight: '200px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Paper>
                                )}

                                {/* Portrait Image */}
                                {product.image_url.portrait && (
                                    <Paper sx={{ p: 1, border: '1px solid #e0e0e0' }}>
                                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Portrait</Typography>
                                        <CachedImage
                                            src={product.image_url.portrait}
                                            alt={`${product.product_name} - Portrait`}
                                            style={{
                                                width: '100%',
                                                maxWidth: '300px',
                                                height: 'auto',
                                                maxHeight: '200px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Paper>
                                )}

                                {/* Banner Image */}
                                {product.image_url.banner && (
                                    <Paper sx={{ p: 1, border: '1px solid #e0e0e0' }}>
                                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Banner</Typography>
                                        <CachedImage
                                            src={product.image_url.banner}
                                            alt={`${product.product_name} - Banner`}
                                            style={{
                                                width: '100%',
                                                maxWidth: '300px',
                                                height: 'auto',
                                                maxHeight: '200px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Paper>
                                )}

                                {/* Additional Images */}
                                {product.image_url?.additional_images?.map((imageUrl, index) => (
                                    <Paper key={`additional-${index}`} sx={{ p: 1, border: '1px solid #e0e0e0' }}>
                                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Additional {index + 1}</Typography>
                                        <CachedImage
                                            src={imageUrl}
                                            alt={`${product.product_name} - Additional ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                maxWidth: '300px',
                                                height: 'auto',
                                                maxHeight: '200px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Paper>
                                ))}

                                {!product.image_url?.logo && !product.image_url?.portrait && !product.image_url?.banner && (!product.image_url?.additional_images || product.image_url.additional_images.length === 0) && (
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: 200,
                                        bgcolor: '#f5f5f5',
                                        borderRadius: 1
                                    }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No images available
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Card>
                    )}
                </Grid>
            </Grid>

            {/* Edit Inventory Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
                    Edit Inventory — {product?.product_name || stateData.productName}
                    {inventory?.location ? ` (${inventory.location})` : ''}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {actionError && (
                        <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
                            {actionError}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Quantity on Hand"
                            type="number"
                            value={editFormData.quantity_on_hand ?? ''}
                            onChange={(e) => handleFieldChange('quantity_on_hand', e.target.value)}
                            fullWidth size="small" disabled={actionLoading}
                            inputProps={{ step: '1', min: '0' }}
                            error={Boolean(editQuantityError)}
                            helperText={editQuantityError || `Available: ${editAvailableQuantity}`}
                        />
                        <TextField
                            label="Minimum Stock Level"
                            type="number"
                            value={editFormData.minimum_stock_level ?? ''}
                            onChange={(e) => handleFieldChange('minimum_stock_level', e.target.value)}
                            fullWidth size="small" disabled={actionLoading}
                            inputProps={{ step: '1', min: '0' }}
                        />
                        <TextField
                            label="Maximum Stock Level"
                            type="number"
                            value={editFormData.maximum_stock_level ?? ''}
                            onChange={(e) => handleFieldChange('maximum_stock_level', e.target.value)}
                            fullWidth size="small" disabled={actionLoading}
                            inputProps={{ step: '1', min: '0' }}
                        />
                        <TextField
                            label="Reorder Point"
                            type="number"
                            value={editFormData.reorder_point ?? ''}
                            onChange={(e) => handleFieldChange('reorder_point', e.target.value)}
                            fullWidth size="small" disabled={actionLoading}
                            inputProps={{ step: '1', min: '0' }}
                        />
                        <TextField
                            label="Reserved Quantity"
                            type="number"
                            value={editFormData.reserved_quantity ?? ''}
                            onChange={(e) => handleFieldChange('reserved_quantity', e.target.value)}
                            fullWidth size="small" disabled={actionLoading}
                            inputProps={{
                                step: '1',
                                min: '0',
                                max: String(editFormData.quantity_on_hand || 0),
                            }}
                            error={Boolean(editQuantityError)}
                            helperText={
                                editQuantityError
                                    ? editQuantityError
                                    : `Must not exceed quantity on hand (${editFormData.quantity_on_hand || 0})`
                            }
                        />
                        <TextField
                            label="Selling Price"
                            type="number"
                            value={editFormData.unit_cost ?? ''}
                            onChange={(e) => handleFieldChange('unit_cost', e.target.value)}
                            fullWidth size="small" disabled={actionLoading}
                            inputProps={{ step: '0.01', min: '0.01' }}
                        />
                        <TextField
                            label="MRP"
                            type="number"
                            value={editFormData.unit_price ?? ''}
                            onChange={(e) => handleFieldChange('unit_price', e.target.value)}
                            fullWidth size="small" disabled={actionLoading}
                            inputProps={{ step: '0.01', min: '0.01' }}
                        />
                        <TextField
                            label="Location"
                            value={editFormData.location ?? ''}
                            onChange={(e) => handleFieldChange('location', e.target.value)}
                            fullWidth size="small" disabled={actionLoading}
                            placeholder="e.g., Warehouse A, Shelf 1"
                        />
                        <TextField
                            label="Notes"
                            value={editFormData.notes ?? ''}
                            onChange={(e) => handleFieldChange('notes', e.target.value)}
                            fullWidth multiline rows={3} size="small" disabled={actionLoading}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
                    <Button onClick={handleEditClose} disabled={actionLoading} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleSaveEdit}
                        variant="contained"
                        disabled={actionLoading || Boolean(editQuantityError)}
                        startIcon={actionLoading ? <CircularProgress size={18} /> : <SaveIcon />}
                        sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#d32f2f' } }}
                    >
                        {actionLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default InventoryDetailPage
